// ============================================================================
// `@torre/team` — contrato compartido del módulo
// "Equipo".
// ============================================================================
// Paquete puro, sin dependencias de framework ni de entorno. Provee, para que
// el front (`apps/v5`) y el back (`torre-de-control-api`) compartan una sola
// fuente de verdad:
//
//   • CONSTANTES  — nombres de colecciones Firestore y vocabularios (enums).
//   • TIPOS       — Colaborador, Squad, PayRate, WorkSettlement,
//                   Contratista y ContractorEngagement.
//   • VALIDACIÓN  — validadores puros que devuelven { valid, errors }.
//   • CÁLCULO     — pago a entregable/día, tarifas por rango, bonos, reparto de
//                   squad y liquidación de contratistas (issues AgroCloud#13/#60).
//   • CONTRATO    — las rutas del servicio HTTP (endpoints) del back.
//
// Diseño (playbook de extracción @torre/*, AgroCloud#73): sin `import.meta.env`, sin
// `axios`, sin `firebase`, sin triggers. Solo funciones puras y datos.
// ============================================================================

// ---------------------------------------------------------------------------
// CONSTANTES — colecciones Firestore (issues AgroCloud#11/#12/#13/#60).
// ---------------------------------------------------------------------------

/** Colección Firestore del maestro de colaboradores (AgroCloud#11). */
export const TEAM_MEMBERS_COLLECTION = "team_members";
/** Colección Firestore de squads (AgroCloud#12). */
export const SQUADS_COLLECTION = "squads";
/** Colección Firestore del catálogo de tarifas y sus tarifas (AgroCloud#13). */
export const PAY_RATES_COLLECTION = "pay_rates";
/** Colección Firestore de liquidaciones de equipo (AgroCloud#13). */
export const WORK_SETTLEMENTS_COLLECTION = "work_settlements";
/** Colección Firestore del maestro de contratistas (AgroCloud#60). */
export const CONTRACTORS_COLLECTION = "contractors";
/** Colección Firestore de tareas/servicios ejecutados por contratistas (AgroCloud#60). */
export const CONTRACTOR_ENGAGEMENTS_COLLECTION = "contractor_engagements";

// ---------------------------------------------------------------------------
// VOCABULARIOS (enums) — compartidos por selects, badges y validación.
// ---------------------------------------------------------------------------

/**
 * Clasificación del colaborador (AgroCloud#11). Reutilizable desde tareas: la
 * clasificación es la que habilita a un colaborador como tech lead u
 * DevOps.
 */
export const TEAM_MEMBER_ROLES = [
  "developer",
  "tech_lead",
  "engineering_manager",
  "scrum_master",
  "product_owner",
  "qa_engineer",
  "devops_engineer",
  "designer",
  // Quien vende y quien hace marketing también cobran nómina y también reparten
  // su semana entre proyectos. Antes sólo cabían en `administrative`, que es
  // otra cosa: clasificarlos mal falsea los reportes por área (#741).
  "sales",
  "marketing",
  "freelancer",
  "administrative",
] as const;
export type TeamMemberRole = (typeof TEAM_MEMBER_ROLES)[number];

/** Estado laboral del colaborador (AgroCloud#11). */
export const TEAM_MEMBER_STATUSES = ["active", "inactive", "offboarded"] as const;
export type TeamMemberStatus = (typeof TEAM_MEMBER_STATUSES)[number];

/**
 * Modalidad de pago de una tarifa (#530):
 *   • `hourly`       — horas × tarifa por hora (las horas pueden venir de commits, #528).
 *   • `daily`        — días × tarifa diaria.
 *   • `deliverable`  — entregables/story points × precio único o por rangos.
 *   • `project`      — monto acordado × % de avance − lo ya pagado.
 *   • `fixed_salary` — sueldo fijo × periodos.
 */
export const PAY_MODES = ["hourly", "daily", "deliverable", "project", "fixed_salary"] as const;
export type PayMode = (typeof PAY_MODES)[number];

/** Modalidad con la que nómina calcula el sueldo base de un colaborador (#530). */
export const MEMBER_PAY_MODES = ["hourly", "daily", "fixed_salary"] as const;
export type MemberPayMode = (typeof MEMBER_PAY_MODES)[number];

// ---------------------------------------------------------------------------
// Periodicidad de un sueldo fijo (#1296).
// ---------------------------------------------------------------------------
// Un «rango de sueldo» (tarifa `fixed_salary`) declara cuánto se paga y CADA
// CUÁNTO: «10 000 al mes» y «5 000 por quincena» son el mismo sueldo. La ficha
// del colaborador guarda lo de UN periodo de SU periodicidad (la empresa paga
// quincenal), así que al ligar a una persona con su rango hay que convertir.
//
// Los periodos por mes son los mismos que usa Gastos y la nómina pagada
// (`PAY_PERIODS_PER_MONTH` en `@torre/expenses`): este paquete no depende de
// aquél a propósito (es el contrato puro), así que se repiten aquí y hay una
// prueba que vigila que no se separen.
// ---------------------------------------------------------------------------

export const PAY_FREQUENCIES = ["weekly", "biweekly", "semimonthly", "monthly"] as const;
export type PayFrequency = (typeof PAY_FREQUENCIES)[number];

/** Periodicidad por omisión: la empresa paga por quincena. */
export const DEFAULT_PAY_FREQUENCY: PayFrequency = "semimonthly";

/** Periodos de pago que caben en un mes, por periodicidad. */
export const PAY_PERIODS_PER_MONTH: Record<PayFrequency, number> = {
  weekly: 52 / 12,
  biweekly: 26 / 12,
  semimonthly: 2,
  monthly: 1,
};

/** Periodicidad válida, o la de omisión si viene vacía o desconocida. */
export function normalizePayFrequency(value: unknown): PayFrequency {
  const raw = String(value ?? "").trim();
  return (PAY_FREQUENCIES as readonly string[]).includes(raw) ? (raw as PayFrequency) : DEFAULT_PAY_FREQUENCY;
}

/** Equivalente mensual de un monto pagado con cierta periodicidad. */
export function monthlyEquivalent(amount: number, frequency: unknown): number {
  return amount * PAY_PERIODS_PER_MONTH[normalizePayFrequency(frequency)];
}

/**
 * Convierte el monto de un periodo de una periodicidad al de otra, pasando por
 * el mensual. Redondea a centavos: una quincena de 10 000 / 2 es 5 000 exactos
 * y una semana de 1 000 × 12 / 52 no debe arrastrar decimales infinitos.
 *
 * Un monto ausente, no numérico o no positivo devuelve `null`: «sin sueldo» no
 * se convierte en cero.
 */
export function salaryForFrequency(amount: unknown, from: unknown, to: unknown): number | null {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const monthly = monthlyEquivalent(n, from);
  const converted = monthly / PAY_PERIODS_PER_MONTH[normalizePayFrequency(to)];
  return Math.round(converted * 100) / 100;
}

/**
 * Sueldo por periodo que le toca a un colaborador según su rango (#1296).
 *
 * El rango es la fuente: trae el monto (`fixedSalary`) y su periodicidad
 * (`payFrequency`, quincenal si no dice); el colaborador aporta la suya. Si la
 * tarifa no es de sueldo fijo o no tiene monto, no hay nada que copiar (`null`).
 */
export function salaryFromRate(
  rate: Pick<PayRate, "mode" | "fixedSalary" | "payFrequency"> | null | undefined,
  memberFrequency: unknown,
): number | null {
  if (!rate || (rate.mode ?? "deliverable") !== "fixed_salary") return null;
  return salaryForFrequency(rate.fixedSalary, rate.payFrequency, memberFrequency);
}

/** Tipo de tarifa por entregable (AgroCloud#13): precio único o por rangos de entregables. */
export const PRICING_TYPES = ["flat", "tiered"] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

/** Estado de una liquidación de equipo (AgroCloud#13). */
export const SETTLEMENT_STATUSES = [
  "draft",
  "calculated",
  "approved",
  "paid",
  "void",
] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

/** Sujeto al que se paga la liquidación: un colaborador o un squad (AgroCloud#12/#13). */
export const SETTLEMENT_SUBJECTS = ["team_member", "squad"] as const;
export type SettlementSubject = (typeof SETTLEMENT_SUBJECTS)[number];

/** Estado de avance de una tarea/servicio de contratista (AgroCloud#60). */
export const CONTRACTOR_ENGAGEMENT_STATUSES = [
  "planned",
  "in_progress",
  "completed",
  "settled",
  "void",
] as const;
export type ContractorEngagementStatus =
  (typeof CONTRACTOR_ENGAGEMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// TIPOS — Colaborador (AgroCloud#11).
// ---------------------------------------------------------------------------

export interface TeamMember {
  id: string;
  /** Nombre de pila. */
  name: string;
  /** Apellidos. */
  lastName?: string | null;
  /** RFC / RFC / identificación. */
  taxId?: string | null;
  role?: TeamMemberRole;
  status?: TeamMemberStatus;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  /** Fecha de ingreso (ISO YYYY-MM-DD). */
  hireDate?: string | null;
  // --- Datos previsionales / laborales básicos ---
  /** Sistema/entidad de pensiones (Afore, IMSS, …). */
  pensionFund?: string | null;
  /** Entidad de salud. */
  health?: string | null;
  /** Nº de contrato o de afiliación previsional. */
  contractNumber?: string | null;
  /** Cómo se calcula su sueldo base en nómina (#530). Por defecto `daily`. */
  payMode?: MemberPayMode;
  /** Tarifa por hora del colaborador (modalidad `hourly`). */
  hourlyRate?: number | null;
  /** Sueldo fijo por periodo de nómina (modalidad `fixed_salary`). */
  fixedSalary?: number | null;
  /**
   * Cada cuánto cobra ESTA persona (#1296). `fixedSalary` es lo de un periodo
   * de esta periodicidad. Sin dato se asume la de la empresa: quincenal.
   */
  payFrequency?: PayFrequency | null;
  /**
   * Rango de sueldo al que pertenece (#1296): id de una tarifa `fixed_salary`
   * del catálogo. Cuando está ligado, el servidor copia de la tarifa la
   * modalidad, la moneda y el sueldo (convertido a `payFrequency`), y los
   * vuelve a copiar cada vez que la tarifa cambia. `null` = sueldo capturado a
   * mano, sin rango.
   */
  payRateId?: string | null;
  /** Nombre del rango, desnormalizado para listarlo sin otra consulta. */
  payRateName?: string | null;
  /** Tarifa diaria de referencia del colaborador. */
  dailyRate?: number | null;
  /**
   * Moneda en la que se le paga a ESTA persona (ISO 4217: "MXN", "USD"…).
   *
   * No es cosmética: en el mismo equipo conviven sueldos en pesos y en dólares,
   * y antes la moneda sólo existía en la tarifa o en el periodo de nómina, con
   * "MXN" como valor por omisión. Sin este dato, `20000` y `300` se pintan con
   * el mismo símbolo y se suman entre sí, que es exactamente el error que no
   * puede cometer un módulo de sueldos (#741).
   *
   * Sigue sin haber tipo de cambio: por eso los totales de un conjunto con
   * varias monedas NO se suman — ver `sumByCurrency`.
   */
  currency?: string | null;
  /** Horas de trabajo contratadas por semana (40, 48…). Base de las asignaciones (#573). */
  weeklyCapacityHours?: number | null;
  /** Reparto de su capacidad semanal entre proyectos (#573). */
  projectAllocations?: ProjectAllocation[];
  /** Sede/campo al que está asignado por defecto (por convención). */
  locationId?: string | null;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Asignación a proyectos (#573).
// ---------------------------------------------------------------------------

/** Capacidad semanal por defecto cuando el colaborador no tiene una propia. */
export const DEFAULT_WEEKLY_CAPACITY_HOURS = 40;

/**
 * Parte de la capacidad semanal de un colaborador dedicada a un proyecto.
 * Ej.: 10 % a uno, 20 % a otro y 70 % a otro.
 */
export interface ProjectAllocation {
  /** Identificador estable del proyecto (slug del nombre si no hay catálogo). */
  projectId: string;
  projectName: string;
  /** Porcentaje de la capacidad semanal (1–100). */
  percentage: number;
  /** Repos `owner/repo` del proyecto: con ellos se miden las horas reales por commits. */
  repos?: string[];
  /** Vigencia opcional (ISO YYYY-MM-DD, inclusive). */
  startDate?: string | null;
  endDate?: string | null;
}

export interface AllocationHours extends ProjectAllocation {
  /** Horas esperadas por semana = capacidad × porcentaje. */
  expectedWeeklyHours: number;
}

// ---------------------------------------------------------------------------
// TIPOS — Squad (AgroCloud#12).
// ---------------------------------------------------------------------------

/** Miembro de un squad (referencia denormalizada al colaborador). */
export interface SquadMember {
  teamMemberId: string;
  /** Nombre denormalizado para listados. */
  name?: string | null;
  role?: TeamMemberRole | null;
}

export interface Squad {
  id: string;
  /** Nombre del squad. */
  name: string;
  /** Número de squad usado en las tareas. */
  number?: number | null;
  /** Colaborador que lo lidera (cualquiera activo; ya no exige la clasificación `tech_lead`). */
  leadId?: string | null;
  /** Nombre del jefe denormalizado para listados. */
  leadName?: string | null;
  members?: SquadMember[];
  /** Proyecto donde opera habitualmente (por convención). */
  projectId?: string | null;
  active?: boolean;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Tarifa de pago (AgroCloud#13).
// ---------------------------------------------------------------------------

/**
 * Tramo de precio por entregables. `to === null` representa un tramo
 * abierto (sin tope superior). El intervalo es [desde, hasta].
 */
export interface TierRate {
  from: number;
  to?: number | null;
  /** Precio unitario aplicable dentro del tramo. */
  price: number;
}

export interface PayRate {
  id: string;
  /** Nombre de la tarea (refactor, release, QA…). */
  name: string;
  /** Código interno de la tarea. */
  code?: string | null;
  mode?: PayMode;
  /**
   * Puesto al que paga la tarifa (#1364): mismo vocabulario que `role` del
   * colaborador (developer, tech_lead, designer, sales…). Un puesto puede
   * tener varias tarifas («Desarrollador nivel 1» y «nivel 2»); al ligar a
   * alguien con un rango, su ficha toma el puesto del rango.
   */
  position?: TeamMemberRole | null;
  /** Solo aplica a `mode: "deliverable"`. */
  pricingType?: PricingType;
  /** Unidad del entregable (historia, story point, ticket, pantalla…). */
  outputUnit?: string | null;
  /** Precio por unidad de entregable cuando `pricingType: "flat"`. */
  unitPrice?: number | null;
  /** Tramos de precio cuando `pricingType: "tiered"`. */
  tiers?: TierRate[];
  /** Tarifa diaria cuando `mode: "daily"`. */
  dailyRate?: number | null;
  /** Tarifa por hora cuando `mode: "hourly"`. */
  hourlyRate?: number | null;
  /** Monto acordado del proyecto cuando `mode: "project"`. */
  agreedAmount?: number | null;
  /** Sueldo por periodo cuando `mode: "fixed_salary"`. */
  fixedSalary?: number | null;
  /**
   * Periodicidad de `fixedSalary` cuando `mode: "fixed_salary"` (#1296): un
   * rango «al mes» se declara `monthly`; sin dato se lee como quincenal.
   */
  payFrequency?: PayFrequency | null;
  /** Entregables mínimos a partir del cual se paga el bono. */
  bonusMinOutput?: number | null;
  /** Monto del bono por cumplir el entregables mínimos. */
  bonusAmount?: number | null;
  /** Centro de costo por defecto al que se imputa la tarea. */
  costCenterId?: string | null;
  currency?: string;
  active?: boolean;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Liquidación de equipo (AgroCloud#13).
// ---------------------------------------------------------------------------

export interface WorkSettlement {
  id: string;
  /** Fecha de la tarea (ISO YYYY-MM-DD). */
  date?: string | null;
  /** A quién se le paga: un colaborador o un squad completo. */
  subject?: SettlementSubject;
  teamMemberId?: string | null;
  teamMemberName?: string | null;
  squadId?: string | null;
  squadName?: string | null;
  /** Tarifa/tarea aplicada. */
  payRateId: string;
  payRateName?: string | null;
  mode?: PayMode;
  /** Entregables completados (solo `deliverable`). */
  output?: number | null;
  /** Días trabajados (solo `daily`). */
  days?: number | null;
  /** Horas trabajadas (solo `hourly`). */
  hours?: number | null;
  /** % de avance del proyecto (solo `project`). */
  progressPercentage?: number | null;
  /**
   * Monto ya pagado del proyecto antes de esta liquidación (solo `project`).
   * Lo resuelve el servidor acumulando las liquidaciones vigentes de la misma
   * tarifa; nunca es un dato que el cliente pueda declarar.
   */
  previouslyPaid?: number | null;
  /** Periodos de sueldo liquidados (solo `fixed_salary`; por defecto 1). */
  periods?: number | null;
  /** Precio unitario resuelto en el momento del cálculo (trazabilidad). */
  appliedUnitPrice?: number | null;
  /** Pago antes de bono. */
  subtotal?: number | null;
  /** Bono por meta aplicado. */
  bonus?: number | null;
  /** Bono/ajuste manual adicional. */
  manualBonus?: number | null;
  /** Total a pagar (subtotal + bono + manualBonus). */
  total?: number | null;
  /** Proyecto al que se imputa el costo del equipo. */
  projectId?: string | null;
  /** Centro de costo al que se imputa el costo del equipo. */
  costCenterId?: string | null;
  status?: SettlementStatus;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Por qué no se pudo calcular un pago (AgroCloud#13). Un pago que no se puede calcular
 * NO es un pago de 0: es un dato que falta, y así viaja hasta la UI/API.
 */
export const NO_CALC_REASONS = [
  /** La tarifa no define un precio para esos entregables (tramo no cubierto). */
  "no_price_for_output",
  /** La tarifa por día no tiene un valor de jornada utilizable. */
  "no_daily_rate",
  /** Falta los entregables de una tarea por entregable. */
  "no_output",
  /** Faltan los días de una tarea por día. */
  "no_days",
  /** Faltan las horas de una tarea por hora. */
  "no_hours",
  /** La tarifa por hora no tiene una tarifa utilizable. */
  "no_hourly_rate",
  /** Falta el % de avance de un proyecto. */
  "no_progress",
  /** La tarifa por proyecto no tiene monto acordado. */
  "no_agreed_amount",
  /** La tarifa de sueldo fijo no tiene sueldo. */
  "no_salary",
  /**
   * La liquidación de sueldo fijo declara 0 periodos. Un periodo es una unidad
   * de tiempo, no una unidad de producción: "cero periodos" no describe un
   * sueldo de 0, describe una liquidación sin periodo que liquidar.
   */
  "no_periods",
] as const;
export type NoCalcReason = (typeof NO_CALC_REASONS)[number];

/**
 * Resultado del cálculo de un pago de equipo (AgroCloud#13).
 *
 * Regla central — **un dato que no existe no es un cero**: si falta el
 * entregables/las jornadas, o la tarifa no da precio para esos entregables, los
 * importes viajan como `null` y `noCalcReason` explica por qué. "No sé
 * cuánto se le debe" nunca se persiste ni se muestra como "no se le debe nada".
 */
export interface PaymentCalculation {
  mode: PayMode;
  /** Precio unitario, tarifa por hora/día, monto acordado o sueldo; `null` = no resuelto. */
  appliedUnitPrice: number | null;
  /** Pago antes del bono; `null` = no calculable. */
  subtotal: number | null;
  /** Bono por meta derivado de la tarifa (0 = no corresponde bono). */
  bonus: number;
  /** Total final a pagar; `null` = no calculable (≠ 0 = no se le debe nada). */
  total: number | null;
  /** `null` cuando el pago SÍ se pudo calcular. */
  noCalcReason: NoCalcReason | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Contratista y sus tareas/servicios (AgroCloud#60).
// ---------------------------------------------------------------------------

export interface Contractor {
  id: string;
  /** Razón social / nombre del contratista. */
  name: string;
  taxId?: string | null;
  /** Giro o especialidad (Frontend, backend, DevOps…). */
  specialty?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  contact?: string | null;
  active?: boolean;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ContractorEngagement {
  id: string;
  contractorId: string;
  /** Nombre del contratista denormalizado para listados. */
  contractorName?: string | null;
  /** Tarea/servicio contratado. */
  description: string;
  startDate?: string | null;
  endDate?: string | null;
  /** Proyecto donde se ejecuta (imputación del costo). */
  projectId?: string | null;
  /** Centro de costo al que se imputa el costo del contratista. */
  costCenterId?: string | null;
  /** Valor total acordado por la tarea. */
  agreedAmount?: number | null;
  /** Avance de la tarea, 0–100. */
  progressPercentage?: number | null;
  status?: ContractorEngagementStatus;
  /** Monto ya pagado/liquidado al contratista. */
  settledAmount?: number | null;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Resultado del cálculo de liquidación de una tarea de contratista (AgroCloud#60). */
export interface ContractorSettlement {
  agreedAmount: number;
  progressPercentage: number;
  /** Monto ganado según el avance (agreedAmount × avance%). */
  earnedAmount: number;
  settledAmount: number;
  /** Devengado − liquidado (nunca negativo). */
  pendingBalance: number;
}

// ---------------------------------------------------------------------------
// VALIDACIÓN — validadores puros (sin dependencias).
// ---------------------------------------------------------------------------

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const ok = (errors: string[]): ValidationResult => ({
  valid: errors.length === 0,
  errors,
});

const isNonEmptyString = (v: unknown): boolean =>
  typeof v === "string" && v.trim().length > 0;

const isPositiveNumber = (v: unknown): boolean =>
  typeof v === "number" && Number.isFinite(v) && v > 0;

const isNonNegativeNumber = (v: unknown): boolean =>
  typeof v === "number" && Number.isFinite(v) && v >= 0;

/** Slug estable para un proyecto sin catálogo propio. */
export function projectIdFromName(name: string): string {
  return String(name ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** ¿La asignación está vigente en esa fecha (YYYY-MM-DD)? Sin vigencia = siempre. */
export function isAllocationActive(a: Pick<ProjectAllocation, "startDate" | "endDate">, date: string): boolean {
  return (!a.startDate || a.startDate <= date) && (!a.endDate || a.endDate >= date);
}

/** Asignaciones vigentes en una fecha. */
export function activeAllocations<T extends Pick<ProjectAllocation, "startDate" | "endDate">>(allocations: T[] = [], date: string): T[] {
  return allocations.filter((a) => isAllocationActive(a, date));
}

/** Porcentaje asignado (vigente en la fecha) y porcentaje libre. */
export function allocatedPercentage(allocations: ProjectAllocation[] = [], date: string): { allocated: number; free: number } {
  const allocated = round2(activeAllocations(allocations, date).reduce((acc, a) => acc + (Number(a.percentage) || 0), 0));
  return { allocated, free: round2(Math.max(0, 100 - allocated)) };
}

/** Horas esperadas por semana de cada asignación vigente. */
export function computeAllocationHours(
  weeklyCapacityHours: number | null | undefined,
  allocations: ProjectAllocation[] = [],
  date: string,
): AllocationHours[] {
  const capacity = Number(weeklyCapacityHours) > 0 ? Number(weeklyCapacityHours) : DEFAULT_WEEKLY_CAPACITY_HOURS;
  return activeAllocations(allocations, date).map((a) => ({
    ...a,
    expectedWeeklyHours: round2((capacity * (Number(a.percentage) || 0)) / 100),
  }));
}

/** Valida las asignaciones: 1–100 % cada una, sin proyectos repetidos y ≤ 100 % vigentes a la vez. */
export function validateProjectAllocations(allocations: Partial<ProjectAllocation>[] = []): ValidationResult {
  const errors: string[] = [];
  if (!Array.isArray(allocations)) return ok(["projectAllocations debe ser una lista"]);
  const seen = new Set<string>();
  allocations.forEach((a, i) => {
    const label = `asignación ${i + 1}`;
    const id = a.projectId || projectIdFromName(a.projectName ?? "");
    if (!isNonEmptyString(a.projectName) && !isNonEmptyString(a.projectId)) errors.push(`${label}: el proyecto es obligatorio`);
    const pct = Number(a.percentage);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) errors.push(`${label}: el porcentaje debe estar entre 1 y 100`);
    if (a.startDate && a.endDate && a.endDate < a.startDate) errors.push(`${label}: la vigencia termina antes de empezar`);
    if (id && seen.has(id)) errors.push(`${label}: el proyecto está repetido`);
    if (id) seen.add(id);
  });
  // El total máximo de asignaciones simultáneas se alcanza en algún inicio de
  // vigencia (o desde siempre, para las que no tienen fecha).
  const checkpoints = new Set<string>(["0000-01-01"]);
  for (const a of allocations) if (a.startDate) checkpoints.add(a.startDate);
  for (const date of checkpoints) {
    const total = allocations
      .filter((a) => isAllocationActive(a, date))
      .reduce((acc, a) => acc + (Number(a.percentage) || 0), 0);
    if (total > 100 + 1e-9) {
      errors.push(`las asignaciones suman ${round2(total)} %: no pueden pasar de 100 %`);
      break;
    }
  }
  return ok(errors);
}

/** Valida el payload de alta/edición de un colaborador (AgroCloud#11). */
export function validateTeamMember(input: Partial<TeamMember>): ValidationResult {
  const errors: string[] = [];
  if (input.weeklyCapacityHours != null) {
    const cap = Number(input.weeklyCapacityHours);
    if (!Number.isFinite(cap) || cap <= 0 || cap > 168) errors.push("weeklyCapacityHours debe estar entre 1 y 168");
  }
  if (input.projectAllocations != null) errors.push(...validateProjectAllocations(input.projectAllocations).errors);
  if (!isNonEmptyString(input.name)) errors.push("nombre es obligatorio");
  if (
    input.role != null &&
    !TEAM_MEMBER_ROLES.includes(input.role)
  ) {
    errors.push("clasificacion no es válida");
  }
  if (input.status != null && !TEAM_MEMBER_STATUSES.includes(input.status)) {
    errors.push("estado no es válido");
  }
  if (
    input.email != null &&
    input.email !== "" &&
    !/^\S+@\S+\.\S+$/.test(String(input.email))
  ) {
    errors.push("email no tiene un formato válido");
  }
  if (input.dailyRate != null && !isNonNegativeNumber(Number(input.dailyRate))) {
    errors.push("dailyRate no puede ser negativo");
  }
  if (input.hourlyRate != null && !isNonNegativeNumber(Number(input.hourlyRate))) {
    errors.push("hourlyRate no puede ser negativo");
  }
  if (input.fixedSalary != null && !isNonNegativeNumber(Number(input.fixedSalary))) {
    errors.push("fixedSalary no puede ser negativo");
  }
  if (input.payMode != null && !MEMBER_PAY_MODES.includes(input.payMode)) {
    errors.push("payMode debe ser 'hourly', 'daily' o 'fixed_salary'");
  }
  if (
    input.payFrequency != null &&
    (input.payFrequency as string) !== "" &&
    !(PAY_FREQUENCIES as readonly string[]).includes(input.payFrequency)
  ) {
    errors.push(`payFrequency debe ser una de: ${PAY_FREQUENCIES.join(", ")}`);
  }
  if (
    input.currency != null &&
    input.currency !== "" &&
    normalizeCurrency(input.currency) == null
  ) {
    errors.push("currency debe ser un código ISO 4217 de tres letras");
  }
  return ok(errors);
}

/** Valida el payload de alta/edición de un squad (AgroCloud#12). */
export function validateSquad(input: Partial<Squad>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyString(input.name)) errors.push("nombre es obligatorio");
  const members = input.members ?? [];
  if (!Array.isArray(members)) {
    errors.push("miembros debe ser una lista");
  } else {
    const seen = new Set<string>();
    members.forEach((m, i) => {
      if (!isNonEmptyString(m?.teamMemberId)) {
        errors.push(`miembro ${i + 1}: teamMemberId es obligatorio`);
        return;
      }
      if (seen.has(m.teamMemberId)) {
        errors.push(`miembro ${i + 1}: colaborador duplicado en el squad`);
      }
      seen.add(m.teamMemberId);
    });
  }
  return ok(errors);
}

/** Valida un tramo de precio por entregables (AgroCloud#13). */
export function validateTierRate(input: Partial<TierRate>): ValidationResult {
  const errors: string[] = [];
  if (!isNonNegativeNumber(Number(input.from))) {
    errors.push("desde no puede ser negativo");
  }
  if (
    input.to != null &&
    (!isNonNegativeNumber(Number(input.to)) ||
      Number(input.to) < Number(input.from))
  ) {
    errors.push("hasta debe ser mayor o igual que desde");
  }
  if (!isNonNegativeNumber(Number(input.price))) {
    errors.push("precio no puede ser negativo");
  }
  return ok(errors);
}

/** Valida el payload de alta/edición de una tarifa de pago (AgroCloud#13). */
export function validatePayRate(input: Partial<PayRate>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyString(input.name)) errors.push("nombre de la tarea es obligatorio");

  const mode = input.mode ?? "deliverable";
  if (!PAY_MODES.includes(mode)) {
    errors.push(`mode debe ser uno de: ${PAY_MODES.join(", ")}`);
    return ok(errors);
  }
  if (
    input.position != null &&
    (input.position as string) !== "" &&
    !(TEAM_MEMBER_ROLES as readonly string[]).includes(input.position)
  ) {
    errors.push(`position debe ser uno de: ${TEAM_MEMBER_ROLES.join(", ")}`);
  }

  if (mode === "daily") {
    if (!isPositiveNumber(Number(input.dailyRate))) {
      errors.push("dailyRate debe ser mayor a 0 para una tarifa por día");
    }
    return ok(errors);
  }
  if (mode === "hourly") {
    if (!isPositiveNumber(Number(input.hourlyRate))) {
      errors.push("hourlyRate debe ser mayor a 0 para una tarifa por hora");
    }
    return ok(errors);
  }
  if (mode === "project") {
    if (!isPositiveNumber(Number(input.agreedAmount))) {
      errors.push("agreedAmount debe ser mayor a 0 para una tarifa por proyecto");
    }
    return ok(errors);
  }
  if (mode === "fixed_salary") {
    if (!isPositiveNumber(Number(input.fixedSalary))) {
      errors.push("fixedSalary debe ser mayor a 0 para una tarifa de sueldo fijo");
    }
    if (
      input.payFrequency != null &&
      (input.payFrequency as string) !== "" &&
      !(PAY_FREQUENCIES as readonly string[]).includes(input.payFrequency)
    ) {
      errors.push(`payFrequency debe ser una de: ${PAY_FREQUENCIES.join(", ")}`);
    }
    return ok(errors);
  }

  // mode === "deliverable"
  const pricingType = input.pricingType ?? "flat";
  if (!PRICING_TYPES.includes(pricingType)) {
    errors.push("pricingType debe ser 'flat' o 'tiered'");
    return ok(errors);
  }

  if (pricingType === "flat") {
    if (!isPositiveNumber(Number(input.unitPrice))) {
      errors.push("unitPrice debe ser mayor a 0 para una tarifa de precio único");
    }
  } else {
    const tiers = input.tiers ?? [];
    if (!Array.isArray(tiers) || tiers.length === 0) {
      errors.push("una tarifa por rango debe tener al menos un tramo");
    } else {
      tiers.forEach((r, i) => {
        const res = validateTierRate(r);
        if (!res.valid) errors.push(`tramo ${i + 1}: ${res.errors.join(", ")}`);
      });
      const overlapping = findOverlappingTiers(tiers);
      if (overlapping) {
        errors.push(
          `los tramos ${overlapping[0] + 1} y ${overlapping[1] + 1} se solapan`,
        );
      }
    }
  }
  return ok(errors);
}

/** Valida el payload de una liquidación de equipo (AgroCloud#13). */
export function validateSettlement(
  input: Partial<WorkSettlement>,
): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyString(input.payRateId)) {
    errors.push("payRateId es obligatorio");
  }

  const subject = input.subject ?? "team_member";
  if (!SETTLEMENT_SUBJECTS.includes(subject)) {
    errors.push("sujeto debe ser 'colaborador' o 'squad'");
  } else if (subject === "team_member" && !isNonEmptyString(input.teamMemberId)) {
    errors.push("teamMemberId es obligatorio cuando el sujeto es 'colaborador'");
  } else if (subject === "squad" && !isNonEmptyString(input.squadId)) {
    errors.push("squadId es obligatorio cuando el sujeto es 'squad'");
  }

  const mode = input.mode ?? "deliverable";
  if (!PAY_MODES.includes(mode)) {
    errors.push(`mode debe ser uno de: ${PAY_MODES.join(", ")}`);
  } else if (mode === "deliverable") {
    if (!isNonNegativeNumber(Number(input.output))) {
      errors.push("entregables no puede ser negativo en una tarea por entregable");
    }
  } else if (mode === "daily") {
    if (!isPositiveNumber(Number(input.days))) {
      errors.push("jornadas debe ser mayor a 0 en una tarea por día");
    }
  } else if (mode === "hourly") {
    if (!isPositiveNumber(Number(input.hours))) {
      errors.push("horas debe ser mayor a 0 en una tarea por hora");
    }
  } else if (mode === "project") {
    const progress = Number(input.progressPercentage);
    if (input.progressPercentage == null || !Number.isFinite(progress) || progress < 0 || progress > 100) {
      errors.push("progressPercentage debe estar entre 0 y 100 en una liquidación por proyecto");
    }
    if (input.previouslyPaid != null && !isNonNegativeNumber(Number(input.previouslyPaid))) {
      errors.push("previouslyPaid no puede ser negativo");
    }
  } else if (mode === "fixed_salary") {
    // La modalidad de sueldo fijo se valida explícitamente, igual que las
    // otras cuatro: sin esta rama quedaba colgada del `else` final y era la
    // única que nadie comprobaba por su nombre. Periodos ausentes = 1 periodo;
    // periodos declarados tienen que ser > 0 (0 periodos no es un sueldo de 0).
    if (input.periods != null && !isPositiveNumber(Number(input.periods))) {
      errors.push("periods debe ser mayor a 0 en una liquidación de sueldo fijo");
    }
  }

  if (input.status != null && !SETTLEMENT_STATUSES.includes(input.status)) {
    errors.push("estado no es válido");
  }
  return ok(errors);
}

/** Valida el payload de alta/edición de un contratista (AgroCloud#60). */
export function validateContractor(
  input: Partial<Contractor>,
): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyString(input.name)) errors.push("nombre es obligatorio");
  if (
    input.email != null &&
    input.email !== "" &&
    !/^\S+@\S+\.\S+$/.test(String(input.email))
  ) {
    errors.push("email no tiene un formato válido");
  }
  return ok(errors);
}

/** Valida el payload de una tarea/servicio de contratista (AgroCloud#60). */
export function validateContractorEngagement(
  input: Partial<ContractorEngagement>,
): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyString(input.contractorId)) {
    errors.push("contractorId es obligatorio");
  }
  if (!isNonEmptyString(input.description)) {
    errors.push("descripcion de la tarea es obligatoria");
  }
  if (!isPositiveNumber(Number(input.agreedAmount))) {
    errors.push("agreedAmount debe ser mayor a 0");
  }
  if (input.progressPercentage != null) {
    const progress = Number(input.progressPercentage);
    if (!Number.isFinite(progress) || progress < 0 || progress > 100) {
      errors.push("progressPercentage debe estar entre 0 y 100");
    }
  }
  if (
    input.status != null &&
    !CONTRACTOR_ENGAGEMENT_STATUSES.includes(input.status)
  ) {
    errors.push("estado no es válido");
  }
  return ok(errors);
}

// ---------------------------------------------------------------------------
// CÁLCULO — lógica pura de pago a entregable/día (AgroCloud#13). Front y back comparten
// estas funciones para que la vista previa y el documento persistido coincidan.
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Número SOLO si el valor es realmente un número; `null` si el dato falta.
 *
 * `Number(null)` es `0` —y `Number.isFinite(0)` es `true`—, así que usar
 * `Number(...)` directo convierte "no hay precio" en "el precio es 0", que este
 * módulo jamás debe hacer: un precio ausente es un dato que falta, y `null` y
 * `undefined` tienen que comportarse igual.
 */
const finiteNumberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Devuelve el par de índices del primer solapamiento entre tramos, o `null`.
 * Los tramos se comparan como intervalos cerrados [desde, hasta] (con
 * `to == null` = infinito).
 */
export function findOverlappingTiers(
  tiers: Partial<TierRate>[],
): [number, number] | null {
  const norm = tiers.map((r) => ({
    from: Number(r.from) || 0,
    to: r.to == null ? Number.POSITIVE_INFINITY : Number(r.to),
  }));
  for (let i = 0; i < norm.length; i++) {
    for (let j = i + 1; j < norm.length; j++) {
      const a = norm[i];
      const b = norm[j];
      if (a.from <= b.to && b.from <= a.to) return [i, j];
    }
  }
  return null;
}

/**
 * Resuelve el precio unitario aplicable a un entregables dado (AgroCloud#13).
 *
 * - `flat` → siempre `unitPrice`.
 * - `tiered`        → el precio del tramo [desde, hasta] que contiene al
 *                    entregables (`to == null` = tramo abierto).
 *
 * Devuelve `null` si ningún tramo cubre los entregables o si el precio
 * aplicable no es un número (la tarifa está incompleta): un precio ausente
 * —`null`, `undefined` o `""`— NO es un precio de 0. Quien llama decide si eso
 * es un error o un pago no calculable.
 */
export function resolvePriceForOutput(
  rate: Pick<PayRate, "pricingType" | "unitPrice" | "tiers">,
  output: number,
): number | null {
  const pricingType = rate.pricingType ?? "flat";
  if (pricingType === "flat") {
    return finiteNumberOrNull(rate.unitPrice);
  }

  const r = Number(output) || 0;
  const tiers = rate.tiers ?? [];
  // Se recorre de menor a mayor `from` para que, ante tramos redundantes,
  // gane siempre el más específico/bajo de forma determinista.
  const sorted = [...tiers].sort(
    (a, b) => (Number(a.from) || 0) - (Number(b.from) || 0),
  );
  for (const tier of sorted) {
    const from = Number(tier.from) || 0;
    const to = tier.to == null ? Number.POSITIVE_INFINITY : Number(tier.to);
    if (r >= from && r <= to) {
      return finiteNumberOrNull(tier.price);
    }
  }
  return null;
}

/**
 * Bono por meta: se paga íntegro si los entregables alcanza el mínimo
 * definido en la tarifa. Sin mínimo o sin monto → 0.
 */
export function computeOutputBonus(
  rate: Pick<PayRate, "bonusMinOutput" | "bonusAmount">,
  output: number,
): number {
  const minOutput = rate.bonusMinOutput;
  const amount = Number(rate.bonusAmount) || 0;
  if (minOutput == null || amount <= 0) return 0;
  return (Number(output) || 0) >= Number(minOutput) ? round2(amount) : 0;
}

/** Entrada del cálculo de un pago de equipo. */
export interface PaymentInput {
  /** Entregables completados (modalidad `deliverable`). */
  output?: number | null;
  /** Días trabajados (modalidad `daily`). */
  days?: number | null;
  /** Horas trabajadas (modalidad `hourly`). */
  hours?: number | null;
  /** % de avance (modalidad `project`). */
  progressPercentage?: number | null;
  /**
   * Monto ya pagado del proyecto (modalidad `project`).
   *
   * El backend NO acepta este valor del cliente: lo calcula acumulando los
   * subtotales de las liquidaciones vigentes de la misma tarifa. Si el cliente
   * lo manda, se ignora. En el front sirve para la vista previa en vivo, que
   * debe alimentarlo con lo ya liquidado para no anticipar un pago duplicado.
   */
  previouslyPaid?: number | null;
  /** Periodos de sueldo (modalidad `fixed_salary`; por defecto 1). */
  periods?: number | null;
  /** Bono/ajuste manual adicional, sumado al total. */
  manualBonus?: number | null;
}

/** Cantidad declarada por el usuario: número finito ≥ 0, o `null` si no la hay. */
function quantityDeclared(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Pago no calculable: importes en `null` (nunca 0) + el motivo. */
function noCalc(
  mode: PayMode,
  reason: NoCalcReason,
  bonus = 0,
): PaymentCalculation {
  return {
    mode,
    appliedUnitPrice: null,
    subtotal: null,
    bonus,
    total: null,
    noCalcReason: reason,
  };
}

/**
 * Calcula el pago de una tarea **por entregable** (AgroCloud#13):
 *   subtotal = entregables × precio(entregables)
 *   total    = subtotal + bono por meta + bono manual
 *
 * Devuelve un pago **no calculable** (importes `null`) si falta los entregables
 * o si la tarifa no define precio para esos entregables. Un entregables de 0 SÍ
 * es calculable: significa "trabajó y no produjo", y paga 0 + bono manual.
 */
export function computeDeliverablePayment(
  rate: Pick<
    PayRate,
    "pricingType" | "unitPrice" | "tiers" | "bonusMinOutput" | "bonusAmount"
  >,
  input: PaymentInput = {},
): PaymentCalculation {
  const output = quantityDeclared(input.output);
  if (output === null) return noCalc("deliverable", "no_output");

  const price = resolvePriceForOutput(rate, output);
  const bonus = computeOutputBonus(rate, output);
  // Sin precio no se sabe cuánto se le debe. Devolver 0 aquí sería pagarle 0
  // por una tarifa mal configurada, en silencio.
  if (price === null) return noCalc("deliverable", "no_price_for_output", bonus);

  const subtotal = round2(output * price);
  const manualBonus = round2(Number(input.manualBonus) || 0);
  return {
    mode: "deliverable",
    appliedUnitPrice: round2(price),
    subtotal,
    bonus,
    total: round2(subtotal + bonus + manualBonus),
    noCalcReason: null,
  };
}

/**
 * Calcula el pago de una tarea **por día** (AgroCloud#13):
 *   subtotal = jornadas × dailyRate
 *   total    = subtotal + bono manual
 *
 * El bono por meta de la tarifa también se evalúa si la tarea por día
 * reporta entregables (bono de producción sobre un pago por jornada).
 *
 * Devuelve un pago **no calculable** (importes `null`) si faltan las jornadas o
 * si la tarifa no tiene un valor de jornada utilizable.
 */
export function computeDailyPayment(
  rate: Pick<PayRate, "dailyRate" | "bonusMinOutput" | "bonusAmount">,
  input: PaymentInput = {},
): PaymentCalculation {
  const days = quantityDeclared(input.days);
  const bonus =
    input.output == null
      ? 0
      : computeOutputBonus(rate, Number(input.output) || 0);
  if (days === null) return noCalc("daily", "no_days", bonus);

  const dailyRate = Number(rate.dailyRate);
  // Una tarifa por día sin valor de jornada no paga 0: no se puede calcular.
  if (!Number.isFinite(dailyRate) || dailyRate <= 0) {
    return noCalc("daily", "no_daily_rate", bonus);
  }

  const subtotal = round2(days * dailyRate);
  const manualBonus = round2(Number(input.manualBonus) || 0);
  return {
    mode: "daily",
    appliedUnitPrice: round2(dailyRate),
    subtotal,
    bonus,
    total: round2(subtotal + bonus + manualBonus),
    noCalcReason: null,
  };
}

/**
 * Calcula el pago **por hora** (#530):
 *   subtotal = horas × hourlyRate
 *   total    = subtotal + bono por meta (si reporta entregables) + bono manual
 *
 * Horas de 0 SÍ son calculables (pagan 0 + bono manual); horas ausentes o una
 * tarifa sin valor por hora dejan el pago **no calculable**.
 */
export function computeHourlyPayment(
  rate: Pick<PayRate, "hourlyRate" | "bonusMinOutput" | "bonusAmount">,
  input: PaymentInput = {},
): PaymentCalculation {
  const hours = quantityDeclared(input.hours);
  const bonus = input.output == null ? 0 : computeOutputBonus(rate, Number(input.output) || 0);
  if (hours === null) return noCalc("hourly", "no_hours", bonus);

  const hourlyRate = Number(rate.hourlyRate);
  if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) {
    return noCalc("hourly", "no_hourly_rate", bonus);
  }

  const subtotal = round2(hours * hourlyRate);
  const manualBonus = round2(Number(input.manualBonus) || 0);
  return {
    mode: "hourly",
    appliedUnitPrice: round2(hourlyRate),
    subtotal,
    bonus,
    total: round2(subtotal + bonus + manualBonus),
    noCalcReason: null,
  };
}

/**
 * Calcula el pago **por proyecto** (#530):
 *   devengado = agreedAmount × min(avance, 100) / 100
 *   subtotal  = max(0, devengado − previouslyPaid)
 *   total     = subtotal + bono manual
 */
export function computeProjectPayment(
  rate: Pick<PayRate, "agreedAmount">,
  input: PaymentInput = {},
): PaymentCalculation {
  const agreedAmount = Number(rate.agreedAmount);
  if (!Number.isFinite(agreedAmount) || agreedAmount <= 0) {
    return noCalc("project", "no_agreed_amount");
  }
  const progress = quantityDeclared(input.progressPercentage);
  if (progress === null) return noCalc("project", "no_progress");

  const earned = round2((agreedAmount * Math.min(100, progress)) / 100);
  const previouslyPaid = round2(Number(input.previouslyPaid) || 0);
  const subtotal = round2(Math.max(0, earned - previouslyPaid));
  const manualBonus = round2(Number(input.manualBonus) || 0);
  return {
    mode: "project",
    appliedUnitPrice: round2(agreedAmount),
    subtotal,
    bonus: 0,
    total: round2(subtotal + manualBonus),
    noCalcReason: null,
  };
}

/**
 * Lo ya liquidado de una tarifa **por proyecto** (#530): la suma de los
 * subtotales de las liquidaciones vigentes de esa misma tarifa.
 *
 * Es el `previouslyPaid` real. Sin acumularlo, dos liquidaciones seguidas al
 * 50 % pagaban el 50 % dos veces: cada una calculaba "devengado − lo que el
 * cliente dijo que ya se pagó", y el cliente no dice nada.
 *
 * Se suma `subtotal` y no `total` a propósito: el `total` incluye el bono
 * manual, que es un extra y no un abono contra el monto acordado.
 * Las liquidaciones anuladas (`void`) no cuentan, igual que en
 * `computeLaborCost`; los subtotales `null` (pagos no calculables) tampoco.
 */
export function computeProjectPaidSoFar(
  settlements: Pick<WorkSettlement, "id" | "payRateId" | "status" | "subtotal">[] = [],
  payRateId: string,
  excludeSettlementId?: string | null,
): number {
  const paid = settlements
    .filter((l) => l.payRateId === payRateId)
    .filter((l) => l.status !== "void")
    .filter((l) => !excludeSettlementId || l.id !== excludeSettlementId)
    .reduce((acc, l) => acc + (finiteNumberOrNull(l.subtotal) ?? 0), 0);
  return round2(paid);
}

/**
 * Calcula el pago de **sueldo fijo** (#530):
 *   subtotal = fixedSalary × periodos (por defecto 1)
 *   total    = subtotal + bono manual
 *
 * Periodos **no declarados** = 1 periodo (el caso normal: liquidar el periodo
 * corriente). Periodos **declarados en 0** dejan el pago no calculable
 * (`no_periods`): sustituirlos por 1 pagaba un periodo que nadie trabajó. No es
 * lo mismo que 0 entregables —que sí paga 0, porque "produjo nada" es un hecho—:
 * un periodo es tiempo, y cero tiempo no es un sueldo de cero.
 */
export function computeFixedSalaryPayment(
  rate: Pick<PayRate, "fixedSalary">,
  input: PaymentInput = {},
): PaymentCalculation {
  const salary = Number(rate.fixedSalary);
  if (!Number.isFinite(salary) || salary <= 0) {
    return noCalc("fixed_salary", "no_salary");
  }
  const declared = quantityDeclared(input.periods);
  if (declared === 0) return noCalc("fixed_salary", "no_periods");
  const periods = declared === null ? 1 : declared;
  const subtotal = round2(salary * periods);
  const manualBonus = round2(Number(input.manualBonus) || 0);
  return {
    mode: "fixed_salary",
    appliedUnitPrice: round2(salary),
    subtotal,
    bonus: 0,
    total: round2(subtotal + manualBonus),
    noCalcReason: null,
  };
}

/**
 * Punto de entrada único del cálculo de equipo (AgroCloud#13 / #530): despacha
 * por la modalidad de la tarifa (o la forzada en el input).
 */
export function computeSettlementPayment(
  rate: PayRate | Partial<PayRate>,
  input: PaymentInput & { mode?: PayMode } = {},
): PaymentCalculation {
  const mode = input.mode ?? rate.mode ?? "deliverable";
  switch (mode) {
    case "hourly":
      return computeHourlyPayment(rate, input);
    case "daily":
      return computeDailyPayment(rate, input);
    case "project":
      return computeProjectPayment(rate, input);
    case "fixed_salary":
      return computeFixedSalaryPayment(rate, input);
    default:
      return computeDeliverablePayment(rate, input);
  }
}

/**
 * Reparte el pago de un squad en partes iguales entre `n` miembros (AgroCloud#12).
 * El remanente de redondeo se asigna al primer miembro para que la suma de las
 * partes sea exactamente el total.
 *
 * QUÉ PERSISTE EL MÓDULO (decisión explícita, AgroCloud#12):
 * asignar un squad crea **una sola** liquidación, con sujeto `squad` y el total
 * completo. Este reparto es **informativo**: `POST /squadAssign` lo devuelve en
 * `memberShares` para que la UI muestre cuánto le tocaría a cada miembro, pero
 * el módulo NO crea una liquidación por miembro. Repartir de verdad significaría
 * duplicar el costo (la del squad + las de los miembros) o cambiar el sujeto de
 * la liquidación, y ambas cosas rompen `computeLaborCost` y los informes de
 * costo que ya consumen `work_settlements`. Si en el futuro se quiere pagar
 * miembro a miembro, se liquida a cada colaborador por separado.
 */
export function splitSquadPayment(total: number, members: number): number[] {
  const n = Math.trunc(Number(members) || 0);
  if (n <= 0) return [];
  const t = round2(Number(total) || 0);
  const base = Math.floor((t / n) * 100) / 100;
  const parts = new Array<number>(n).fill(base);
  parts[0] = round2(base + (t - round2(base * n)));
  return parts;
}

/**
 * Liquidación de una tarea de contratista (AgroCloud#60): lo devengado según el avance
 * menos lo ya pagado. `pendingBalance` nunca es negativo (un sobrepago se
 * refleja como saldo 0).
 */
export function computeContractorSettlement(
  engagement: Pick<
    ContractorEngagement,
    "agreedAmount" | "progressPercentage" | "settledAmount"
  >,
): ContractorSettlement {
  const agreedAmount = round2(Number(engagement.agreedAmount) || 0);
  const progressRaw = Number(engagement.progressPercentage);
  const progressPercentage = Number.isFinite(progressRaw)
    ? Math.min(100, Math.max(0, progressRaw))
    : 0;
  const settledAmount = round2(Number(engagement.settledAmount) || 0);
  const earnedAmount = round2((agreedAmount * progressPercentage) / 100);
  return {
    agreedAmount,
    progressPercentage,
    earnedAmount,
    settledAmount,
    pendingBalance: round2(Math.max(0, earnedAmount - settledAmount)),
  };
}

/** Costo total de equipo de un conjunto de liquidaciones (AgroCloud#13). */
export function computeLaborCost(
  settlements: Pick<WorkSettlement, "total" | "status">[] = [],
): number {
  const total = settlements
    .filter((l) => l.status !== "void")
    .reduce((acc, l) => acc + (Number(l.total) || 0), 0);
  return round2(total);
}

/**
 * Agrupa el costo del equipo por proyecto/centro de costo (AgroCloud#13): la
 * imputación que consumen los informes de costos.
 *
 * `groupBy` es el CAMPO por el que se agrupa (no un "código"): sólo cambia la
 * dimensión de corte, no el contrato persistido.
 */
export function computeCostByAllocation(
  settlements: Pick<
    WorkSettlement,
    "total" | "status" | "projectId" | "costCenterId"
  >[] = [],
  groupBy: "projectId" | "costCenterId" = "projectId",
): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const l of settlements) {
    if (l.status === "void") continue;
    const key = l[groupBy] ?? "unallocated";
    acc[key] = round2((acc[key] ?? 0) + (Number(l.total) || 0));
  }
  return acc;
}

// ---------------------------------------------------------------------------
// MONEDA (#741)
// ---------------------------------------------------------------------------

/** Moneda por omisión cuando un documento no declara la suya. */
export const DEFAULT_CURRENCY = "MXN";

/**
 * Normaliza un código de moneda a ISO 4217 en mayúsculas. Devuelve `null` si no
 * se declaró: "no sé en qué moneda" no es "en pesos", igual que `null ≠ 0`.
 */
export function normalizeCurrency(raw?: string | null): string | null {
  const code = String(raw ?? "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/**
 * Suma importes **por moneda**, sin convertir nada.
 *
 * No hay tipo de cambio en la plataforma, así que un total único sobre importes
 * en pesos y en dólares sería un número que no significa nada — y aquí ese
 * número es el costo de la gente. Por eso se devuelve un total por moneda y
 * `mixed`, para que quien pinte sepa que no puede mostrar una sola cifra.
 */
export function sumByCurrency(
  items: { amount?: number | null; currency?: string | null }[] = [],
  fallbackCurrency: string | null = DEFAULT_CURRENCY,
): { totals: Record<string, number>; mixed: boolean; unknownCurrency: number } {
  const totals: Record<string, number> = {};
  let unknownCurrency = 0;
  for (const item of items) {
    const amount = Number(item.amount);
    if (!Number.isFinite(amount)) continue;
    const code = normalizeCurrency(item.currency) ?? normalizeCurrency(fallbackCurrency);
    if (!code) {
      unknownCurrency += 1;
      continue;
    }
    totals[code] = round2((totals[code] ?? 0) + amount);
  }
  return { totals, mixed: Object.keys(totals).length > 1, unknownCurrency };
}

/** Nombre completo del colaborador, listo para listados y denormalización. */
export function fullName(
  teamMember: Pick<TeamMember, "name" | "lastName">,
): string {
  return [teamMember.name, teamMember.lastName].filter(Boolean).join(" ").trim();
}

/** ¿El colaborador puede liderar un squad? (AgroCloud#12) */
export function canBeTechLead(
  teamMember: Pick<TeamMember, "role" | "status">,
): boolean {
  return (
    teamMember.role === "tech_lead" &&
    teamMember.status !== "offboarded"
  );
}

// ---------------------------------------------------------------------------
// CONTRATO DE SERVICIO — rutas HTTP del back (torre-de-control-api
// `teamMembers`). Front y back comparten estas rutas para no divergir.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Bonos individuales con vigencia (#1365).
// ---------------------------------------------------------------------------
// Un bono es un extra de UNA persona con fecha de inicio y de fin obligatorias:
//   • `per_period`: el monto se paga en CADA periodo de nómina que caiga dentro
//     de la vigencia.
//   • `range`: el monto es el TOTAL de toda la vigencia y se reparte en partes
//     iguales entre los periodos de nómina que abarca.
// Fuera de la vigencia no se paga nada: ni se adelanta ni se arrastra. El
// backend reproduce esta lógica en `modules/team/bonusMath.js`.
// ---------------------------------------------------------------------------

/** Colección Firestore de los bonos individuales (#1365). */
export const TEAM_MEMBER_BONUSES_COLLECTION = "team_member_bonuses";

export const BONUS_MODES = ["per_period", "range"] as const;
export type BonusMode = (typeof BONUS_MODES)[number];

export interface TeamMemberBonus {
  id: string;
  teamMemberId: string;
  /** Desnormalizado para listar sin otra consulta. */
  teamMemberName?: string | null;
  /** Qué se paga: «bono de resultados Q4», «guardia», «proyecto X»… */
  concept: string;
  /** Por periodo (`per_period`) o total de toda la vigencia (`range`). */
  amount: number;
  /** Moneda del bono; por omisión la de la persona. */
  currency?: string | null;
  mode?: BonusMode;
  /** Vigencia obligatoria (ISO YYYY-MM-DD), `startDate` ≤ `endDate`. */
  startDate: string;
  endDate: string;
  active?: boolean;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const isIsoDay = (v: unknown): boolean => /^\d{4}-\d{2}-\d{2}$/.test(String(v ?? ""));
const AVG_DAYS_PER_MONTH = 365.25 / 12;

/** Días entre dos fechas ISO, contando ambas («del 1 al 15» son 15 días). */
export function daysInclusive(from: string, to: string): number {
  const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.floor((b - a) / 86400000) + 1;
}

/** Valida el payload de alta/edición de un bono (#1365). */
export function validateTeamMemberBonus(input: Partial<TeamMemberBonus>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyString(input.teamMemberId)) errors.push("teamMemberId es obligatorio");
  if (!isNonEmptyString(input.concept)) errors.push("concept es obligatorio");
  if (!isPositiveNumber(Number(input.amount))) errors.push("amount debe ser mayor a 0");
  if (input.mode != null && (input.mode as string) !== "" && !(BONUS_MODES as readonly string[]).includes(input.mode)) {
    errors.push(`mode debe ser uno de: ${BONUS_MODES.join(", ")}`);
  }
  if (!isIsoDay(input.startDate) || !isIsoDay(input.endDate)) {
    errors.push("un bono necesita fecha de inicio y de fin (YYYY-MM-DD)");
  } else if ((input.endDate as string) < (input.startDate as string)) {
    errors.push("la fecha de fin no puede ser anterior al inicio");
  }
  if (input.currency != null && input.currency !== "" && normalizeCurrency(input.currency) == null) {
    errors.push("currency debe ser un código ISO 4217 de tres letras");
  }
  return ok(errors);
}

/** Periodos de nómina que abarca una vigencia (nunca menos de 1). */
export function bonusPeriodsInRange(startDate: string, endDate: string, frequency: unknown): number {
  if (!isIsoDay(startDate) || !isIsoDay(endDate) || endDate < startDate) return 1;
  return Math.max(1, Math.round((daysInclusive(startDate, endDate) / AVG_DAYS_PER_MONTH) * PAY_PERIODS_PER_MONTH[normalizePayFrequency(frequency)]));
}

/** ¿El bono está vigente en esa fecha? (dado de baja = nunca vigente). */
export function bonusIsActiveOn(bonus: Pick<TeamMemberBonus, "startDate" | "endDate" | "active">, date: string): boolean {
  if (!bonus || bonus.active === false) return false;
  if (!isIsoDay(bonus.startDate) || !isIsoDay(bonus.endDate) || !isIsoDay(date)) return false;
  return bonus.startDate <= date && date <= bonus.endDate;
}

/** ¿La vigencia toca el periodo [periodStart, periodEnd]? */
export function bonusOverlapsPeriod(
  bonus: Pick<TeamMemberBonus, "startDate" | "endDate" | "active">,
  periodStart: string,
  periodEnd: string,
): boolean {
  if (!bonus || bonus.active === false) return false;
  if (![bonus.startDate, bonus.endDate, periodStart, periodEnd].every(isIsoDay)) return false;
  return bonus.startDate <= periodEnd && periodStart <= bonus.endDate;
}

/**
 * Lo que se paga de este bono en UN periodo de nómina, o `null` si no toca.
 * `frequency` es la periodicidad con la que cobra la persona (reparto del rango).
 */
export function bonusAmountForPeriod(
  bonus: Pick<TeamMemberBonus, "startDate" | "endDate" | "active" | "amount" | "mode">,
  periodStart: string,
  periodEnd: string,
  frequency: unknown,
): number | null {
  if (!bonusOverlapsPeriod(bonus, periodStart, periodEnd)) return null;
  const amount = Number(bonus.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if ((bonus.mode ?? "per_period") === "range") {
    return round2(amount / bonusPeriodsInRange(bonus.startDate, bonus.endDate, frequency));
  }
  return round2(amount);
}

/** Bonos de una persona que tocan un periodo, con lo que toca pagar de cada uno. */
export function bonusesForPeriod<T extends TeamMemberBonus>(
  bonuses: readonly T[],
  teamMemberId: string,
  periodStart: string,
  periodEnd: string,
  frequency: unknown,
): { bonus: T; amount: number }[] {
  const out: { bonus: T; amount: number }[] = [];
  for (const b of bonuses) {
    if (!b || b.teamMemberId !== teamMemberId) continue;
    const amount = bonusAmountForPeriod(b, periodStart, periodEnd, frequency);
    if (amount == null) continue;
    out.push({ bonus: b, amount });
  }
  return out;
}

/**
 * Lo que un bono suma a UN mes (`YYYY-MM`) para la estimación de Gastos: los
 * periodos de nómina del mes que caen en la vigencia × lo que toca por periodo.
 */
export function bonusMonthlyAmount(
  bonus: Pick<TeamMemberBonus, "startDate" | "endDate" | "active" | "amount" | "mode">,
  month: string,
  frequency: unknown,
): number {
  if (!/^\d{4}-\d{2}$/.test(String(month || ""))) return 0;
  const year = Number(month.slice(0, 4));
  const mon = Number(month.slice(5, 7));
  const daysInMonth = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  const monthStart = `${month}-01`;
  const monthEnd = `${month}-${String(daysInMonth).padStart(2, "0")}`;
  const perPeriod = bonusAmountForPeriod(bonus, monthStart, monthEnd, frequency);
  if (perPeriod == null) return 0;
  const from = bonus.startDate > monthStart ? bonus.startDate : monthStart;
  const to = bonus.endDate < monthEnd ? bonus.endDate : monthEnd;
  const periods = Math.max(1, Math.round((daysInclusive(from, to) / daysInMonth) * PAY_PERIODS_PER_MONTH[normalizePayFrequency(frequency)]));
  return round2(perPeriod * periods);
}

export const TEAM_ENDPOINTS = {
  // Colaboradores (AgroCloud#11)
  teamMembersList: "/teamMembersList",
  teamMember: "/teamMember",
  // Bonos individuales con vigencia (#1365)
  teamBonusList: "/teamBonusList",
  teamBonus: "/teamBonus",
  // Squads (AgroCloud#12)
  squadsList: "/squadsList",
  squads: "/squads",
  squadAssign: "/squadAssign",
  // Tarifas de pago (AgroCloud#13)
  payRatesList: "/payRatesList",
  payRate: "/payRate",
  // Liquidaciones de equipo (AgroCloud#13)
  workSettlementsList: "/workSettlementsList",
  workSettlement: "/workSettlement",
  workSettlementCalculation: "/workSettlementCalculation",
  // Contratistas (AgroCloud#60)
  contractorsList: "/contractorsList",
  contractors: "/contractors",
  // Tareas/servicios de contratista (AgroCloud#60)
  contractorEngagementsList: "/contractorEngagementsList",
  contractorEngagement: "/contractorEngagement",
  contractorEngagementSettle: "/contractorEngagementSettle",
  // Resumen del módulo
  teamSummary: "/teamSummary",
} as const;

export type TeamEndpoint =
  (typeof TEAM_ENDPOINTS)[keyof typeof TEAM_ENDPOINTS];

/** Versión del contrato, útil para trazas/diagnóstico. */
export const TORRE_TEAM_VERSION = "0.1.0";
